import axios from 'axios';
import React, { ChangeEvent, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import CardHeader from 'react-bootstrap/CardHeader';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import {
    FaFileImport,
    FaMessage,
    FaRegCircleXmark,
    FaRegFloppyDisk,
    FaRegPenToSquare,
    FaRegSquarePlus,
    FaRegTrashCan,
} from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { parseCsv } from '../../../../../helpers/csv';
import { IMeeting, IMember } from '../../../../../types';
import { IconWrapper } from '../../../../IconWrapper/IconWrapper';
import styles from './Members.module.scss';

interface IMembersProps {
  meeting: IMeeting;
  onChange: () => void;
}

const Members = ({ meeting, onChange }: IMembersProps) => {
  const [newFirstname, setNewFirstname] = useState<string>('');
  const [newLastname, setNewLastname] = useState<string>('');
  const [newPhone, setNewPhone] = useState<string>('');
  const [updateMemberId, setUpdateMemberId] = useState<string>('');
  const [updatedFirstname, setUpdatedFirstname] = useState<string>('');
  const [updatedLastname, setUpdatedLastname] = useState<string>('');
  const [updatedPhone, setUpdatedPhone] = useState<string>('');
  const [updatedProxy, setUpdatedProxy] = useState<string>('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  const handleSave = () => {
    axios
      .post(`/api/meeting/${meeting._id}/member`, {
        firstname: newFirstname,
        lastname: newLastname,
        phone: newPhone,
      })
      .then(() => {
        setNewFirstname('');
        setNewLastname('');
        setNewPhone('');
        onChange();
      })
      .catch((err) => toast.error(err.response.data.message));
  };

  const handleImport = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    file
      .text()
      .then((text) => {
        const rows = parseCsv(text);
        if (rows.some((row) => !row.firstname || !row.lastname)) {
          throw new Error('CSV members must contain firstname and lastname columns');
        }
        return axios.post(`/api/meeting/${meeting._id}/member/import`, rows);
      })
      .then(() => onChange())
      .catch((error) =>
        toast.error(error.response?.data ?? error.message ?? 'Unable to import members'),
      )
      .finally(() => {
        event.target.value = '';
      });
  };

  const handleDelete = (id: string) => () => {
    axios
      .delete(`/api/meeting/${meeting._id}/member/${id}`)
      .then(() => {
        onChange();
      })
      .catch((err) => toast.error(err.response.data.message));
  };

  const handleBulkDelete = () => {
    if (
      selectedMemberIds.length === 0 ||
      !window.confirm(`Delete ${selectedMemberIds.length} members?`)
    )
      return;
    axios
      .post(`/api/meeting/${meeting._id}/member/delete`, { ids: selectedMemberIds })
      .then(() => {
        setSelectedMemberIds([]);
        onChange();
      })
      .catch((err) => toast.error(err.response?.data ?? 'Unable to delete members'));
  };

  const toggleMember = (id: string) => {
    setSelectedMemberIds((current) =>
      current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id],
    );
  };

  const toggleAllMembers = () => {
    setSelectedMemberIds(
      selectedMemberIds.length === meeting.members.length
        ? []
        : meeting.members.map((member) => member._id),
    );
  };

  const handleEdit = (member: IMember | undefined) => () => {
    setUpdateMemberId(member ? member._id : '');

    if (member) {
      setUpdatedFirstname(member.firstname);
      setUpdatedLastname(member.lastname);
      setUpdatedPhone(member.phone);
      setUpdatedProxy(member.proxy ?? '');
    } else {
      setUpdatedFirstname('');
      setUpdatedLastname('');
      setUpdatedPhone('');
      setUpdatedProxy('');
    }
  };

  const handleMemberResponseChange =
    (
      member: IMember,
      type:
        | 'response'
        | 'present'
        | 'candidate_local_office'
        | 'candidate_local_council'
        | 'candidate',
    ) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const updatedMember = member;

      if (type === 'candidate') {
        updatedMember['candidate_local_office'] = e.target.checked;
        updatedMember['candidate_local_council'] = e.target.checked;
      } else {
        updatedMember[type] = e.target.checked;
      }

      axios
        .put(`/api/meeting/${meeting._id}/member/${member._id}`, updatedMember)
        .then(() => {
          onChange();
        })
        .catch((err) => toast.error(err.response.data.message));
    };

  const handleEditSaveChange = (member: IMember) => () => {
    const updatedMember = member;
    updatedMember.firstname = updatedFirstname;
    updatedMember.lastname = updatedLastname;
    updatedMember.phone = updatedPhone;

    if (updatedProxy !== '') {
      updatedMember.proxy = updatedProxy;
    } else {
      updatedMember.proxy = undefined;
    }

    axios.put(`/api/meeting/${meeting._id}/member/${member._id}`, updatedMember).then(() => {
      setUpdateMemberId('');
      setUpdatedFirstname('');
      setUpdatedLastname('');
      setUpdatedPhone('');
      setUpdatedProxy('');
      onChange();
    });
  };

  const handleSendMessage = () => {
    axios
      .post(
        `/api/meeting/${meeting._id}/member/message`,
        {
          message: '',
        },
        { params: { filter: 'all' } },
      )
      .catch((err) => toast.error(err.response.data.message));
  };

  const sortByAttribute = (attr: 'lastname' | 'firstname') => (a: IMember, b: IMember) => {
    if (a[attr] < b[attr]) {
      return -1;
    }
    if (a[attr] > b[attr]) {
      return 1;
    }
    return 0;
  };

  const getProxyName = (member: IMember) => {
    if (member.proxy) {
      const proxy = meeting.members.find((p) => p._id === member.proxy);

      if (proxy) {
        return `${proxy.firstname} ${proxy.lastname}`;
      }
    }
    return '';
  };

  return (
    <>
      <Card>
        <CardHeader className="d-flex justify-content-between">
          <Col> Members ({meeting.members.length})</Col>
          <Col className="d-flex justify-content-end gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={handleBulkDelete}
              disabled={
                selectedMemberIds.length === 0 ||
                meeting.parameters.global.block_responses ||
                meeting.parameters.global.block_candidacies
              }
            >
              Delete selected ({selectedMemberIds.length})
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={toggleAllMembers}>
              {selectedMemberIds.length === meeting.members.length
                ? 'Clear selection'
                : 'Select all'}
            </Button>
            <Button size="sm" onClick={handleSendMessage} variant="secondary">
              <IconWrapper icon={FaMessage} /> Send Message
            </Button>
            <Form.Label className="btn btn-secondary btn-sm mb-0">
              <IconWrapper icon={FaFileImport} /> Import CSV
              <Form.Control type="file" accept=".csv,text/csv" onChange={handleImport} hidden />
            </Form.Label>
          </Col>
        </CardHeader>
        <Card.Body>
          <Table striped hover bordered size="sm" className="text-center">
            <thead className="align-middle">
              <tr>
                <th rowSpan={2} className={styles.wsmall}>
                  Select
                </th>
                <th colSpan={3}>Personnal information</th>
                <th colSpan={3}>Response</th>
                {meeting.parameters.global.candidate_once ? (
                  <th rowSpan={2}>Candidate</th>
                ) : (
                  <th colSpan={2}>Candidate</th>
                )}
                <th rowSpan={2} className={styles.wsmall}>
                  Actions
                </th>
              </tr>
              <tr>
                <th className={styles.wlarge}>First name</th>
                <th className={styles.wlarge}>Last name</th>
                <th className={styles.wlarge}>Phone number</th>
                <th className={styles.wsmall}>Response</th>
                <th className={styles.wsmall}>Present</th>
                <th className={styles.wlarge}>Proxy</th>
                {meeting.parameters.global.candidate_once ? null : (
                  <>
                    <th className={styles.wsmall}>Local office</th>
                    <th className={styles.wsmall}>Local council</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="align-middle">
              {meeting.members.length === 0 && (
                <tr>
                  <td colSpan={meeting.parameters.global.candidate_once ? 9 : 10}>No members</td>
                </tr>
              )}
              {meeting.members.length > 0 &&
                meeting.members
                  .sort(sortByAttribute('firstname'))
                  .sort(sortByAttribute('lastname'))
                  .map((member) => (
                    <React.Fragment key={member._id}>
                      {member._id !== updateMemberId && (
                        <tr>
                          <td>
                            <Form.Check
                              checked={selectedMemberIds.includes(member._id)}
                              onChange={() => toggleMember(member._id)}
                              aria-label={`Select ${member.firstname} ${member.lastname}`}
                            />
                          </td>
                          <td>{member.firstname}</td>
                          <td>{member.lastname}</td>
                          <td>{member.phone}</td>
                          <td>
                            <Form.Check
                              checked={member.response ?? false}
                              onChange={handleMemberResponseChange(member, 'response')}
                              disabled={meeting.parameters.global.block_responses}
                            />
                          </td>
                          <td>
                            <Form.Check
                              checked={member.present ?? false}
                              onChange={handleMemberResponseChange(member, 'present')}
                              disabled={meeting.parameters.global.block_responses}
                            />
                          </td>
                          <td>{getProxyName(member)}</td>
                          {meeting.parameters.global.candidate_once ? (
                            <td>
                              <Form.Check
                                checked={member.candidate_local_office ?? false}
                                onChange={handleMemberResponseChange(member, 'candidate')}
                                disabled={meeting.parameters.global.block_candidacies}
                              />
                            </td>
                          ) : (
                            <>
                              <td>
                                <Form.Check
                                  checked={member.candidate_local_office ?? false}
                                  onChange={handleMemberResponseChange(
                                    member,
                                    'candidate_local_office',
                                  )}
                                  disabled={meeting.parameters.global.block_candidacies}
                                />
                              </td>
                              <td>
                                <Form.Check
                                  checked={member.candidate_local_council ?? false}
                                  onChange={handleMemberResponseChange(
                                    member,
                                    'candidate_local_council',
                                  )}
                                  disabled={meeting.parameters.global.block_candidacies}
                                />
                              </td>
                            </>
                          )}
                          <td>
                            <ButtonGroup aria-label="Actions">
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={handleEdit(member)}
                                title="Edit"
                              >
                                <IconWrapper icon={FaRegPenToSquare} />
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={handleDelete(member._id)}
                                title="Delete"
                                disabled={
                                  meeting.parameters.global.block_responses ||
                                  meeting.parameters.global.block_candidacies
                                }
                              >
                                <IconWrapper icon={FaRegTrashCan} />
                              </Button>
                            </ButtonGroup>
                          </td>
                        </tr>
                      )}
                      {member._id === updateMemberId && (
                        <tr>
                          <td>
                            <Form.Check
                              checked={selectedMemberIds.includes(member._id)}
                              onChange={() => toggleMember(member._id)}
                              aria-label={`Select ${member.firstname} ${member.lastname}`}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className="text-center"
                              value={updatedFirstname}
                              onChange={(e) => setUpdatedFirstname(e.target.value)}
                              placeholder="First name"
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className="text-center"
                              value={updatedLastname}
                              onChange={(e) => setUpdatedLastname(e.target.value)}
                              placeholder="Last name"
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className="text-center"
                              value={updatedPhone}
                              onChange={(e) => setUpdatedPhone(e.target.value)}
                              placeholder="Phone number"
                            />
                          </td>
                          <td>
                            <Form.Check
                              checked={member.response ?? false}
                              onChange={handleMemberResponseChange(member, 'response')}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              checked={member.present ?? false}
                              onChange={handleMemberResponseChange(member, 'present')}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Select
                              aria-label="Select proxy name"
                              size="sm"
                              onChange={(e) =>
                                setUpdatedProxy(e.target.value === '0' ? '' : e.target.value)
                              }
                              className="text-center"
                              value={updatedProxy}
                              disabled={meeting.parameters.global.block_responses}
                            >
                              <option value={0}>No proxy</option>
                              {meeting.members
                                .filter((m) => m._id !== member._id)
                                .map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {m.firstname} {m.lastname}
                                  </option>
                                ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Check
                              checked={member.candidate_local_office ?? false}
                              onChange={handleMemberResponseChange(
                                member,
                                'candidate_local_office',
                              )}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              checked={member.candidate_local_council ?? false}
                              onChange={handleMemberResponseChange(
                                member,
                                'candidate_local_council',
                              )}
                              disabled
                            />
                          </td>
                          <td>
                            <ButtonGroup aria-label="Actions">
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={handleEditSaveChange(member)}
                                title="Save"
                              >
                                <IconWrapper icon={FaRegFloppyDisk} />
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleEdit(undefined)}
                                title="Cancel"
                              >
                                <IconWrapper icon={FaRegCircleXmark} />
                              </Button>
                            </ButtonGroup>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newFirstname}
                    onChange={(e) => setNewFirstname(e.target.value)}
                    placeholder="First name"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newLastname}
                    onChange={(e) => setNewLastname(e.target.value)}
                    placeholder="Last name"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="Phone number"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td colSpan={meeting.parameters.global.candidate_once ? 4 : 5}></td>
                <td>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    title="Add"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  >
                    <IconWrapper icon={FaRegSquarePlus} />
                  </Button>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

export default Members;
